import React, { useContext, useState, useEffect } from "react";
import { AddressSearchContext } from "../../context/addressSearchContext.js";
import InputField from "../comman/input.js";
import Button from "../comman/button.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import { YMAP_KEY } from "../../constants/index.js";

const AddressSearch = ({ onApplyAddress }) => {
    const { language } = useLanguage();
    const { selectedAddress, onSelectAddress, onSetIsUserInteracting } = useContext(AddressSearchContext);
    const [value, setValue] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [apiKeyError, setApiKeyError] = useState(false);

    // Check if API key is configured
    useEffect(() => {
        if (!YMAP_KEY || YMAP_KEY.trim() === '') {
            console.error('Yandex Maps API key is not configured. Please set REACT_APP_YMAP_KEY environment variable.');
            setApiKeyError(true);
        }
    }, []);

    // Debounce search
    useEffect(() => {
        if (!value) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const timeoutId = setTimeout(async () => {
            // Don't make API call if API key is missing
            if (!YMAP_KEY || YMAP_KEY.trim() === '') {
                console.error('Cannot fetch address suggestions: Yandex Maps API key is not configured');
                setApiKeyError(true);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setApiKeyError(false);
            try {
                const url = new URL("https://geocode-maps.yandex.ru/1.x");
                url.searchParams.set("apikey", YMAP_KEY);
                url.searchParams.set("geocode", value);
                url.searchParams.set("results", "5");
                url.searchParams.set("lang", `${language === 'am' ? 'hy' : language}_AM`);
                url.searchParams.set("format", "json");
                url.searchParams.set("ll", "44.491567,40.153759");
                url.searchParams.set("spn", "0.473785400390625,0.7407931148263032");

                const res = await fetch(url);
                if (!res.ok) {
                    // Check for 400 Bad Request which typically indicates invalid API key
                    if (res.status === 400) {
                        const errorData = await res.json().catch(() => ({}));
                        const errorMessage = errorData.error?.message || res.statusText;
                        console.error('Yandex Maps API error (400):', errorMessage);
                        console.error('This usually indicates an invalid or expired API key, or the key is not authorized for this domain.');
                        setApiKeyError(true);
                        throw new Error(`Invalid API key or configuration: ${errorMessage}`);
                    }
                    throw new Error(`Yandex API error: ${res.status} ${res.statusText}`);
                }
                
                const data = await res.json();
                
                // Check for API errors in response
                if (data.error) {
                    const errorMessage = data.error.message || "Yandex API returned an error";
                    console.error('Yandex Maps API error in response:', errorMessage);
                    // Check if it's an API key related error
                    if (data.error.code === 403 || errorMessage.toLowerCase().includes('api key') || errorMessage.toLowerCase().includes('apikey')) {
                        setApiKeyError(true);
                    }
                    throw new Error(errorMessage);
                }

                const collection = [];
                if (data.response?.GeoObjectCollection?.featureMember) {
                    data.response.GeoObjectCollection.featureMember.forEach((item) => {
                        try {
                            const geoObject = item.GeoObject;
                            if (!geoObject || !geoObject.Point || !geoObject.Point.pos) {
                                console.warn("Invalid geoObject structure:", item);
                                return;
                            }
                            const point = geoObject.Point.pos.split(" ");
                            if (point.length < 2) {
                                console.warn("Invalid point coordinates:", geoObject.Point.pos);
                                return;
                            }
                            collection.push({
                                address: geoObject.metaDataProperty?.GeocoderMetaData?.text || "Unknown address",
                                location: [Number(point[1]), Number(point[0])] // [lat, lng]
                            });
                        } catch (itemError) {
                            console.error("Error processing geoObject item:", itemError);
                        }
                    });
                }
                setSuggestions(collection);
                setShowSuggestions(true);
            } catch (error) {
                console.error("Error fetching address suggestions:", error);
                // Convert non-Error objects to Error instances
                if (!(error instanceof Error)) {
                    const errorMessage = error?.message || JSON.stringify(error) || "Unknown error";
                    console.error("Non-Error object caught in AddressSearch:", errorMessage);
                }
                setSuggestions([]);
                setShowSuggestions(false);
            } finally {
                setIsLoading(false);
            }
        }, 700);

        return () => clearTimeout(timeoutId);
    }, [value, language]);

    const handleResetInputState = () => {
        setValue("");
        onSelectAddress(null);
        setShowSuggestions(false);
    };

    const handleApplyAddress = () => {
        onApplyAddress(handleResetInputState);
    };

    const handleSelectAddress = (suggestion) => {
        setValue("");
        setShowSuggestions(false);
        onSetIsUserInteracting(false);
        onSelectAddress({
            address: suggestion.address,
            location: suggestion.location
        });
    };

    return (
        <div className="relative w-full flex gap-2">
            <div className="relative flex-1">
                <InputField
                    value={(selectedAddress?.address && !value) ? selectedAddress.address : value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        if (selectedAddress) {
                            onSelectAddress(null);
                        }
                    }}
                    name="addressSearch"
                    placeholder={getTranslation('profile.addressPlaceholder', language) || "Enter address"}
                    labelFor="address-search"
                    className="pr-10"
                />
                {(selectedAddress || value) && (
                    <button
                        type="button"
                        onClick={handleResetInputState}
                        className="absolute right-3 top-[1.25rem] -translate-y-1/2 flex items-center justify-center text-gray-500 dark:text-[#8b949e] hover:text-gray-700 dark:hover:text-[#c9d1d9] transition-colors z-10"
                        aria-label="Clear address"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
                {apiKeyError && (
                    <div className="absolute top-full z-50 w-full rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 shadow-md mt-1 p-4">
                        <div className="text-sm text-red-700 dark:text-red-400">
                            <strong>{getTranslation('common.error', language) || "Error"}:</strong> {getTranslation('profile.apiKeyError', language) || "Yandex Maps API key is not configured or invalid. Please contact support."}
                        </div>
                    </div>
                )}
                {showSuggestions && value && !apiKeyError && (
                    <div className="absolute top-full z-50 w-full rounded-lg border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#161b22] shadow-md mt-1 max-h-60 overflow-y-auto">
                        {isLoading && (
                            <div className="p-4 text-center text-gray-500 dark:text-[#8b949e]">
                                {getTranslation('common.loading', language) || "Loading..."}
                            </div>
                        )}
                        {!isLoading && suggestions.length === 0 && (
                            <div className="p-4 text-center text-gray-500 dark:text-[#8b949e]">
                                {getTranslation('profile.addressNotFound', language) || "Address not found"}
                            </div>
                        )}
                        {!isLoading && suggestions.map((suggestion, index) => (
                            <div
                                key={index}
                                onClick={() => handleSelectAddress(suggestion)}
                                className="px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-[#1c2128] border-b border-gray-200 dark:border-[#30363d] last:border-b-0 transition-colors"
                            >
                                <span className="text-sm font-medium text-gray-900 dark:text-[#c9d1d9]">{suggestion.address}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <Button
                disabled={!selectedAddress}
                onClick={handleApplyAddress}
                variant="primary"
                size="md"
            >
                {getTranslation('profile.confirm', language) || "Confirm"}
            </Button>
        </div>
    );
};

export default AddressSearch;

